import { useEffect, useState } from "react";
import { api, money, shortDate, type Job, type WorkAsset, type WorkYear } from "../api";
import { Choice, ErrorNotice, InlineForm, Loading } from "../ui";

const gap = (px: number) => ({ "--gap": `${px}px` }) as React.CSSProperties;
const toCents = (text: string | undefined): number | null => {
  const clean = (text ?? "").replace(/[$,\s]/g, "");
  if (!clean) return null;
  const n = Number(clean);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : NaN;
};
const needCents = (text: string | undefined, what: string) => {
  const c = toCents(text);
  if (c === null || Number.isNaN(c)) throw new Error(`Enter ${what} as a number, like 1250.00`);
  return c;
};
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });
const fyLabel = (fy: string) => `${Number(fy.slice(2)) - 1}–${fy.slice(4)}`;
const fyOf = (date: string) => `FY${Number(date.slice(0, 4)) + (Number(date.slice(5, 7)) >= 7 ? 1 : 0)}`;

/** Year picker: this year and the four before it. */
export function YearPicker({ fy, onChange }: { fy: string; onChange: (fy: string) => void }) {
  const current = fyOf(today());
  const years = Array.from({ length: 5 }, (_, i) => `FY${Number(current.slice(2)) - i}`);
  return (
    <select aria-label="Financial year" value={fy} onChange={(e) => onChange(e.target.value)} style={{ width: "auto" }}>
      {years.map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
    </select>
  );
}

function HoursLog({ onChange }: { onChange: () => void }) {
  const [date, setDate] = useState(today());
  const [hours, setHours] = useState("");
  const [log, setLog] = useState<{ date: string; hours: number }[]>([]);
  const [error, setError] = useState<unknown>(null);
  const load = () => {
    const from = new Date(Date.now() - 13 * 86_400_000).toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });
    api.get<{ date: string; hours: number }[]>(`/api/work/home-office/log?from=${from}&to=${today()}`).then(setLog).catch(setError);
  };
  useEffect(load, []);
  return (
    <div className="card flat stack" style={gap(10)}>
      <h3>Log your hours</h3>
      <form className="row wrap" style={{ alignItems: "flex-end" }} onSubmit={async (e) => {
        e.preventDefault();
        try {
          setError(null);
          await api.put(`/api/work/home-office/log/${date}`, { hours: Number(hours) });
          setHours("");
          load();
          onChange();
        } catch (err) { setError(err); }
      }}>
        <div className="field"><label htmlFor="log-date">Day</label><input id="log-date" type="date" max={today()} value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div className="field" style={{ maxWidth: 140 }}><label htmlFor="log-hours">Hours at home</label><input id="log-hours" type="number" min={0} max={24} step={0.25} value={hours} onChange={(e) => setHours(e.target.value)} required /></div>
        <button type="submit" className="btn primary">Save</button>
      </form>
      {log.length > 0 && <p className="muted small">Last two weeks: {log.map((l) => `${shortDate(l.date)} ${l.hours}h`).join(" · ")}. Enter 0 to remove a day.</p>}
      <ErrorNotice error={error} />
    </div>
  );
}

export function Work() {
  const [fy, setFy] = useState(fyOf(today()));
  const [year, setYear] = useState<WorkYear | null>(null);
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [assets, setAssets] = useState<WorkAsset[]>([]);
  const [error, setError] = useState<unknown>(null);
  const loadYear = () => api.get<WorkYear>(`/api/work/${fy}`).then(setYear).catch(setError);
  const loadJobs = () => api.get<Job[]>("/api/jobs").then(setJobs).catch(setError);
  const loadAssets = () => api.get<WorkAsset[]>("/api/assets?kind=work").then(setAssets).catch(setError);
  useEffect(() => { loadYear(); }, [fy]);
  useEffect(() => { loadJobs(); loadAssets(); }, []);
  if (!year || !jobs) return <><ErrorNotice error={error} /><Loading /></>;

  const setMethod = async (method: "fixed_rate" | "actual", hours?: number | null) => {
    try {
      setError(null);
      setYear(await api.put<WorkYear>(`/api/work/${fy}/home-office`, { method, hours: hours ?? year.homeOffice.hours }));
    } catch (e) { setError(e); }
  };
  const held = jobs.filter((j) => j.startedOn <= `${fy.slice(2)}-06-30` && (!j.endedOn || j.endedOn >= `${Number(fy.slice(2)) - 1}-07-01`));

  return (
    <div className="stack" style={gap(24)}>
      <div className="row wrap">
        <div>
          <h1>Work</h1>
          <p className="muted">Your jobs and income statements, working from home, and things you bought for work.</p>
        </div>
        <div className="spacer" />
        <YearPicker fy={fy} onChange={setFy} />
      </div>
      <ErrorNotice error={error} />
      {year.checks.map((c, i) => <div key={i} className={`notice${c.level === "warning" ? " warn" : ""}`}>{c.message}</div>)}

      <section className="stack">
        <h2>Jobs</h2>
        {held.length === 0 && <p className="muted">No jobs in {year.label}.</p>}
        {held.map((j) => {
          const s = j.statements.find((x) => x.fy === fy);
          return (
            <div key={j.id} className="card stack" style={gap(10)}>
              <div className="row wrap">
                <div><h3>{j.employer}</h3><span className="muted small">{j.occupation ? `${j.occupation} · ` : ""}from {shortDate(j.startedOn)}{j.endedOn ? ` to ${shortDate(j.endedOn)}` : ""}</span></div>
                <div className="spacer" />
                {s && <span className={`pill${s.taxReady ? " ok" : ""}`}>{s.taxReady ? "Tax ready" : "Not tax ready"}</span>}
              </div>
              {s ? (
                <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
                  <div className="stat"><span className="muted small">Item 1 · Salary</span><strong>{money(s.grossCents)}</strong></div>
                  <div className="stat"><span className="muted small">Tax withheld</span><strong>{money(s.taxWithheldCents)}</strong></div>
                  {s.allowancesCents > 0 && <div className="stat"><span className="muted small">Allowances</span><strong>{money(s.allowancesCents)}</strong></div>}
                  {s.rescCents > 0 && <div className="stat"><span className="muted small">Salary sacrifice to super</span><strong>{money(s.rescCents)}</strong></div>}
                  {s.rfbaCents > 0 && <div className="stat"><span className="muted small">Fringe benefits</span><strong>{money(s.rfbaCents)}</strong></div>}
                </div>
              ) : <p className="muted small">No income statement for {year.label} yet. It's in myGov → ATO → Employment → Income statements, tax ready by mid-July.</p>}
              <InlineForm<Job> title={s ? "Update the income statement" : "Add the income statement"} onDone={() => { loadJobs(); loadYear(); }}
                fields={[
                  { id: "gross", label: "Gross payments", type: "text" }, { id: "tax", label: "Tax withheld", type: "text" },
                  { id: "allowances", label: "Allowances", type: "text", optional: true }, { id: "resc", label: "Reportable employer super", type: "text", optional: true, hint: "Salary sacrifice (RESC)" },
                  { id: "rfba", label: "Reportable fringe benefits", type: "text", optional: true },
                  { id: "ready", label: "Marked tax ready?", type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "Not yet" }] },
                ]}
                submit={(v) => api.put(`/api/jobs/${j.id}/statements/${fy}`, {
                  grossCents: needCents(v.gross, "gross payments"), taxWithheldCents: needCents(v.tax, "tax withheld"),
                  allowancesCents: toCents(v.allowances) ?? 0, rescCents: toCents(v.resc) ?? 0, rfbaCents: toCents(v.rfba) ?? 0, taxReady: v.ready !== "no",
                })} />
            </div>
          );
        })}
        <InlineForm<Job> title="+ Add a job" onDone={() => { loadJobs(); loadYear(); }}
          fields={[
            { id: "employer", label: "Employer", type: "text" }, { id: "occupation", label: "Your role", type: "text", optional: true },
            { id: "startedOn", label: "Started", type: "date" }, { id: "endedOn", label: "Finished", type: "date", optional: true },
          ]}
          submit={(v) => api.post("/api/jobs", { employer: v.employer, occupation: v.occupation || null, startedOn: v.startedOn, endedOn: v.endedOn || null })} />
      </section>

      <section className="stack">
        <div className="row wrap"><h2>Working from home</h2><div className="spacer" />
          <Choice label="Method" value={year.homeOffice.method} onChange={(m) => setMethod(m)} options={[{ value: "fixed_rate", label: "Fixed rate (70c an hour)" }, { value: "actual", label: "Actual costs" }]} />
        </div>
        {!year.homeOffice.method && <p className="muted">Choose how you'll claim it for {year.label}. Most people use the fixed rate: a set amount for each hour, covering energy, phone, internet and stationery.</p>}
        {year.homeOffice.method === "fixed_rate" && (
          <>
            <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
              <div className="stat"><span className="muted small">Hours logged</span><strong>{year.homeOffice.loggedHours.toLocaleString("en-AU")}</strong><span className="muted small">{year.homeOffice.loggedDays} day{year.homeOffice.loggedDays === 1 ? "" : "s"}</span></div>
              <div className="stat"><span className="muted small">Claim</span><strong style={{ color: "var(--accent)" }}>{money(year.homeOffice.claimCents)}</strong></div>
            </div>
            {year.homeOffice.explanation && <p className="muted small">{year.homeOffice.explanation}</p>}
            {fy === fyOf(today()) && <HoursLog onChange={loadYear} />}
            {year.homeOffice.loggedDays === 0 && (
              <div className="field" style={{ maxWidth: 320 }}>
                <label htmlFor="total-hours">Or the total hours from your own records</label>
                <input id="total-hours" type="number" min={0} defaultValue={year.homeOffice.hours ?? ""} onBlur={(e) => setMethod("fixed_rate", e.target.value === "" ? null : Number(e.target.value))} />
              </div>
            )}
          </>
        )}
        {year.homeOffice.method === "actual" && <p className="muted small">{year.homeOffice.explanation} Claim: <strong>{money(year.homeOffice.claimCents)}</strong>.</p>}
      </section>

      <section className="stack">
        <h2>Things you bought for work</h2>
        <p className="muted small">$300 or less: claimed in full the year you buy it. More: claimed over its effective life at your work-use %.</p>
        {assets.length > 0 && (
          <div className="card list-card list">
            {assets.map((a) => {
              const y = a.years.find((x) => x.fy === fy);
              return (
                <div key={a.id} className="list-item">
                  <div style={{ flex: 1 }}>{a.description}<div className="muted small">{money(a.costCents)} on {shortDate(a.acquiredOn)} · {a.effectiveLifeYears} years · {a.currentWorkPct ?? 0}% work{a.sale ? ` · sold ${shortDate(a.sale.disposedOn)}` : ""}</div></div>
                  <span className="amount">{y ? money(y.claim) : "—"}</span>
                </div>
              );
            })}
          </div>
        )}
        <InlineForm<WorkAsset> title="+ Add something you bought" onDone={() => { loadAssets(); loadYear(); }}
          fields={[
            { id: "description", label: "What", type: "text" }, { id: "cost", label: "Cost", type: "text" },
            { id: "acquiredOn", label: "Bought on", type: "date" }, { id: "life", label: "Effective life (years)", type: "number", hint: "Laptop 2, phone 3, desk 10 — the ATO's effective life table" },
            { id: "workPct", label: "Work use %", type: "number" },
          ]}
          submit={(v) => api.post("/api/assets", { kind: "work", description: v.description, costCents: needCents(v.cost, "the cost"), acquiredOn: v.acquiredOn, effectiveLifeYears: Number(v.life), workPct: Number(v.workPct) })} />
      </section>

      <section className="card flat stack" style={gap(6)}>
        <div className="row"><strong>D5 Other work-related expenses</strong><div className="spacer" /><strong>{money(year.d5Cents)}</strong></div>
        <p className="muted small">Working from home, work assets, and phone, internet, tools and subscriptions filed to work. Car, travel, clothing and study receipts go to their own labels.</p>
        {year.receipts.length > 0 && <p className="muted small">Receipts filed to work this year: {year.receipts.map((r) => `${r.name} ${money(r.cents)}${r.excluded ? " (covered by the fixed rate)" : ""}`).join(" · ")}</p>}
      </section>
    </div>
  );
}

